import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, Stack } from "@certn/cascadia";
import { GhostIconButton, Divider } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { ReactNode, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { UmbrellaCheckSection } from "./UmbrellaCheckSection";

interface Props {
  umbrellaChecksSupported: components["schemas"]["CheckListChild"][];
  umbrellaChecksHeading?: string;
  setSelectedUmbrellaCheck: React.Dispatch<
    SetStateAction<components["schemas"]["CheckListChild"] | null>
  >;
  renderFullListModal?: (props: {
    show: boolean;
    onClose: () => void;
  }) => ReactNode;
  fullListCount?: number;
}

export function UmbrellaChecksSection(props: Props) {
  const { t } = useTranslation();
  const {
    umbrellaChecksSupported,
    umbrellaChecksHeading = t("Regions supported"),
    setSelectedUmbrellaCheck,
    renderFullListModal,
    fullListCount,
  } = props;

  const [showFullListModal, setShowFullListModal] = useState(false);

  return (
    <>
      <div
        className={styles.sectionContainer}
        aria-label={umbrellaChecksHeading}
      >
        <Text style="global/heading-large">{umbrellaChecksHeading}</Text>

        {umbrellaChecksSupported.map((umbrellaCheck, key) => (
          <UmbrellaCheckSection
            key={key}
            umbrellaCheck={umbrellaCheck}
            onUmbrellaCheckSelect={setSelectedUmbrellaCheck}
          />
        ))}
        {renderFullListModal && fullListCount && (
          <>
            <div
              className={styles.viewFullListItem}
              onClick={() => setShowFullListModal(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowFullListModal(true);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Stack orientation="horizontal">
                <Text style="global/heading-medium" marginTop="space-4">
                  {t("View full list")} ({fullListCount})
                </Text>
              </Stack>
              <Stack orientation="horizontal">
                <GhostIconButton
                  testID="view-full-list-button"
                  onPress={() => setShowFullListModal(true)}
                  size="medium"
                  icon="chevron_right"
                  aria-label={t("View full list")}
                />
              </Stack>
            </div>
            <Divider />
          </>
        )}
      </div>

      {renderFullListModal &&
        renderFullListModal({
          show: showFullListModal,
          onClose: () => setShowFullListModal(false),
        })}
    </>
  );
}

const styles = {
  sectionContainer: css({
    marginTop: "var(--space-4)",
  }),
  viewFullListItem: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: `${"var(--space-2)"} ${"var(--space-0)"} ${"var(--space-2)"} ${"var(--space-0)"}`,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--background-ghost-hover)",
    },
    "&:focus": {
      outline: "2px solid var(--border-accent-primary-default)",
      outlineOffset: "-2px",
    },
  }),
};
